import { format } from 'date-fns';
import LocalizedStrings from 'react-localization';
import { constructNameEN } from '../../employee/employee-name-helper';
import { withCommon } from '../../services/translation/commonTranslations';

const rosterStrings = new LocalizedStrings({
  en: {
    calendarBreadcrumbTitle: 'Calendar',
    rosterBreadcrumbTitle: 'Rosters',
    cardTitle: 'Rosters',
    published: 'Published',
    completed: 'Completed',
    error: 'Error',
    autofill: 'Auto-Fill',
    queued: 'Queued',
    inProgress: 'In Progress',
    ready: 'Ready',
    draft: 'Draft',
    roster: 'Roster',
    editRoster: 'Edit Roster',
    publishedRoster: 'Published Roster',
    publishButton: {
      draft: 'Publish Roster',
      complete: 'Roster Published',
      edit: 'Edit Roster'
    },
    rosterRequirements: 'Roster Requirements',
    viewRequirements: 'View Requirements',
    positions: {
      add: 'Add position'
    },
    staffSelector: {
      pleaseSelect: 'Please select a staff member:',
      hoursWorked: (number: number) => `${number} hour${number === 1 ? '' : 's'} this month`,
      shiftsWorked: (number: number) => `${number} shift${number === 1 ? '' : 's'} this month`,
      warnings: {
        partialAvailability: 'This employee is only available for part of the shift.',
        noAvailability: 'This employee is not available on this date.',
        rdo: 'This employee has a fixed day off on this date.',
        exceedsShiftMax: "This shift is longer than the employee's maximum contracted hours.",
        exceedsDayMax: 'This shift would cause the employee to work for longer than their maximum daily hours.',
        alreadyAllocated: 'This employee is already allocated.'
      }
    },
    save: {
      success: 'Roster saved successfully!'
    },
    confirmModal: {
      confirmPublish: 'Are you sure you would like to publish this roster? Staff members will be able to see it.',
      publish: 'Publish',
      confirm: 'Confirm',
      confirmEdit:
        'Are you sure you would like to edit this roster? Staff members will not be able to see it until you publish it again.'
    },
    rosterInformation: 'Roster Information',
    employeeName: constructNameEN,
    notAssigned: 'Not assigned',
    noStaff: 'No Staff Available',
    noResults: 'No results',
    noRoster: 'No roster was created for this day.',
    employees: 'Employees',
    create: 'Create',
    saveAndLeave: 'Save and leave',
    createRosterModal: {
      title: 'Create new roster',
      howBusy: 'How busy do you expect the day to be?',
      createMessage: (date: Date) => `This will create a new empty roster for ${format(date, 'MMMM do yyyy')}.`,
      radioOptions: {
        busy: 'Busy',
        normal: 'Normal',
        quiet: 'Quiet'
      }
    },
    deleteRosterConfirmModal: {
      title: 'Delete Roster',
      message: (date: string) => `Are you sure you want to delete the roster on ${date}?`,
      confirm: 'Delete'
    },
    optimiseRosters: {
      title: 'Optimise Rosters',
      startDate: 'Start date',
      endDate: 'End date',
      submitText: 'Create Rosters and Autofill',
      selectRosterDates: 'Which dates would you like to create rosters for?',
      selectTemplate: 'Template To Apply',
      selectTemplates: 'Which roster templates would you like to apply?',
      selectSolver: 'Which optimiser would you like to use?',
      selectSolverDescription: 'Choose which optimisation engine to run for this roster batch.',
      selectStartEndDate: 'Please select a start and end date',
      selectStartEndDateInvalid: 'Start date must be before end date',
      selectStartDateInPast: 'Start date cannot be in the past',
      createTabHeader: 'Create Rosters and Autofill',
      submit: {
        createPeriod: (startDate: Date, endDate: Date) =>
          `Rosters will be created and autofilled for the period between ${format(
            startDate,
            'dd/MM/yyyy'
          )} and ${format(endDate, 'dd/MM/yyyy')}.`,
        selectedSolver: (solverName: string) => `Selected optimiser: ${solverName}.`,
        autofillTime:
          'Autofilling the rosters can take a few minutes. You can see the progress of the optimiser on the rostering screen.',
        start: 'Click create rosters and autofill to get started!'
      },
      solvers: {
        cpSat: {
          label: 'CP-SAT',
          description: 'Uses the CP-SAT solver.'
        },
        geneticAlgorithm: {
          label: 'GA',
          description: 'Uses the legacy genetic algorithm optimiser.'
        }
      },
      levels: {
        busy: 'Busy',
        normal: 'Normal',
        quiet: 'Quiet'
      },
      error: {
        unableToSend: 'Unable to send request. Please try again, or contact technical support.',
        issueWithRequest: 'There was an issue with the request. Please try again, or contact technical support.'
      },
      evaluation: {
        title: 'Latest shift quality summaries',
        historyButtonLabel: 'Show optimisation history',
        description: 'A plain-English summary of the most recent optimisation run covering this month.',
        compareDescription: 'Compare the two most recent optimisation runs covering this month.',
        waiting: 'Waiting for optimisation evaluation...',
        loadError: 'Unable to load optimisation evaluation.',
        noHistory: 'No optimisation history is available for this period.',
        latestRun: 'Most Recent Run',
        previousRun: 'Previous Run',
        calculatedAt: 'Created',
        solver: 'Solver',
        coverage: 'Coverage rate',
        assignedShifts: 'Assigned shifts',
        totalShifts: 'Total planned shifts',
        overallScore: 'Overall score',
        solverStatus: 'Solver Status',
        gap: 'Gap',
        objectiveValue: 'Objective',
        bestBound: 'Best Bound',
        solveDuration: 'Solve duration',
        coverageScore: 'Coverage score',
        assignmentScore: 'Assignment score',
        optimalityScore: 'Optimality score',
        summaryTitle: 'Shift quality summary',
        summaryDescription: 'A clearer summary of what the optimiser achieved for this period.',
        summaryBasis: 'Based on optimiser coverage and allocation results for this run.',
        unfilledShifts: 'Unfilled shifts',
        alwaysRequiredIssues: 'Hard constraint issues',
        improvementAreas: 'Soft constraint issues',
        allPlannedShifts: 'Fill every planned shift',
        rosterCreated: 'Create a roster for the selected period',
        monthlyCoverage: 'Improve cover across the month',
        underCoveredDays: 'Reduce under-covered days',
        allocationQuality: 'Improve allocation quality',
        solverConfidence: 'Improve solver confidence',
        alwaysRequired: 'Always required',
        bestEndeavours: 'Best endeavours',
        technicalDetails: 'Technical details',
        noData: 'No data available',
        noPlannedShifts: 'No planned shifts were found for this period.',
        shiftsStillNeedAssigning: (count: number) =>
          `${count} planned shift${count === 1 ? '' : 's'} still need assigning.`,
        allShiftsAssigned: 'All planned shifts are assigned.',
        rosterAvailable: (count: number) => `Summary available for ${count} planned shift${count === 1 ? '' : 's'}.`,
        monthCoverageStatus: (coverage: string) => `Overall cover for this run is ${coverage}.`,
        underCoveredDaysStatus: (count: number) =>
          count === 0
            ? 'Every day in this run is fully covered.'
            : `${count} day${count === 1 ? '' : 's'} still include open shifts.`,
        allocationQualityStatus: (rating: string) => `Allocation quality is ${rating}.`,
        solverConfidenceStatus: (rating: string) => `Solver confidence is ${rating}.`,
        issuesCount: (count: number) => `${count} issue${count === 1 ? '' : 's'}`,
        milliseconds: (value: number) => `${value.toLocaleString()} ms`
      }
    },
    essMobile: {
      notAvail: 'Not available',
      shift: 'Shift',
      viewRoster: 'View roster',
      noStaff: 'There are no staff on this station.'
    },
    popover: {
      shiftTasks: 'Shift Tasks'
    },
    rosterDateSwitch: {
      prevLabel: 'Previous roster',
      nextLabel: 'Next roster'
    },
    drawer: {
      summary: {
        label: 'Summary',
        timeWorked: 'Time Worked',
        totalStaffRostered: 'Total Staff Rostered',
        totalShifts: 'Total Shifts'
      },
      tasks: {
        label: 'Tasks',
        direction: 'Drag and drop to add new subsections to a shift:',
        break: 'Break'
      },
      warnings: {
        label: 'Warnings',
        majorIssues: (number: number) => `Major issues (${number})`,
        minorIssues: (number: number) => `Minor issues (${number})`,
        nobodyAvailable: (timePeriod: string) => `Nobody is available for the ${timePeriod} shift.`
      }
    }
  }
});

export default withCommon<typeof rosterStrings>(rosterStrings);
